import { useMemo } from 'react';
import { Plane, Quaternion, Vector3 } from 'three';

import {
    isFramiClamp,
    isPlacementIndicator
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EPSILON } from 'shared/constants';

import type { Article } from 'app/modules/wallforming/components/scenes/objects/articles/types/article';
import type { DimensionClamp } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionClamp';
import type { ColliderPoint } from 'app/modules/wallforming/components/scenes/objects/helpers/types/colliderPoint';
import type { Poi } from 'app/modules/wallforming/components/scenes/objects/pois/types/poi';

interface CreateClampDimensioningProps {
    instanceToGroupMap: Record<string, string>;
    articleColliders: ColliderPoint[];
    selectedPoiIndex: number | undefined;
    mid: Vector3;
    workingPlane: Plane | null;
    floorZ: number;
    poi: Poi;
}

interface ClampDimensioningResult {
    markers: DimensionClamp[];
}

const Z_SCALE = 0.4;
const MIN_MARKER_WIDTH = 0.1;
const MIN_MARKER_HEIGHT = 0.04;
const RENDER_ORDER_BASE = 300;
const RENDER_ORDER_LAYERS = 32;

const uAxis = new Vector3();
const normalVector = new Vector3();
const projectedPoint = new Vector3();
const axisRotation = new Quaternion();
const rotatedUAxis = new Vector3();
const rotatedVAxis = new Vector3();

const defaultResult: ClampDimensioningResult = { markers: [] };

interface WorkingPlaneFrame {
    normal: Vector3;
    uAxis: Vector3;
    vAxis: Vector3;
}

interface ClampMarker extends DimensionClamp {
    relationId: string;
    offsetToPlane: number;
}

const isValidWorkingPlane = (workingPlane: Plane): boolean => workingPlane.normal.lengthSq() >= EPSILON;

const createWorkingPlaneFrame = (workingPlane: Plane): WorkingPlaneFrame | null => {
    normalVector.copy(workingPlane.normal);
    if (!isValidWorkingPlane(workingPlane)) {
        return null;
    }

    normalVector.normalize();

    uAxis.set(normalVector.y, -normalVector.x, 0);
    if (uAxis.lengthSq() < EPSILON) {
        uAxis.set(1, 0, 0);
    } else {
        uAxis.normalize();
    }

    return {
        normal: normalVector.clone(),
        uAxis: uAxis.clone(),
        vAxis: new Vector3().crossVectors(normalVector, uAxis).normalize()
    };
};

const buildClampLookup = (
    poiArticles: NonNullable<Poi['articles']>,
    articles: Record<string, Article>,
    instanceToGroupMap: Record<string, string>
): {
    clampRelationIds: Set<string>;
    clampRotationByRelationId: Map<string, number>;
    clampGroupIdByRelationId: Map<string, string | null>;
} => {
    const clampRelationIds = new Set<string>();
    const clampRotationByRelationId = new Map<string, number>();
    const clampGroupIdByRelationId = new Map<string, string | null>();

    for (const [articleId, instanceIds] of Object.entries(poiArticles)) {
        const article = articles[articleId];
        if (!article || !isFramiClamp(article.articleNumber)) {
            continue;
        }

        for (const instanceId of instanceIds) {
            const instance = article.articleInstances[instanceId];
            if (!instance) {
                continue;
            }

            clampRelationIds.add(instanceId);
            clampRotationByRelationId.set(instanceId, Object.values(instance.parts)[0]?.rotation ?? 0);
            clampGroupIdByRelationId.set(instanceId, instanceToGroupMap[instanceId] ?? null);
        }
    }

    return {
        clampRelationIds,
        clampRotationByRelationId,
        clampGroupIdByRelationId
    };
};

const buildClampMarkers = (
    clampColliders: ColliderPoint[],
    workingPlane: Plane,
    frame: WorkingPlaneFrame,
    mid: Vector3,
    zOffset: number,
    clampRotationByRelationId: Map<string, number>,
    clampGroupIdByRelationId: Map<string, string | null>
): ClampMarker[] => {
    const relationExtents = new Map<string, { minU: number; maxU: number; minZ: number; maxZ: number }>();
    const markers: ClampMarker[] = [];

    for (const collider of clampColliders) {
        const signedDistance = workingPlane.distanceToPoint(collider.position);
        projectedPoint.copy(collider.position).addScaledVector(frame.normal, -signedDistance);

        const projectedU =
            projectedPoint.x * frame.uAxis.x + projectedPoint.y * frame.uAxis.y + projectedPoint.z * frame.uAxis.z;
        const projectedZ = projectedPoint.z;

        const existing = relationExtents.get(collider.relationId);
        if (!existing) {
            relationExtents.set(collider.relationId, {
                minU: projectedU,
                maxU: projectedU,
                minZ: collider.position.z,
                maxZ: collider.position.z
            });
        } else {
            if (projectedU < existing.minU) {
                existing.minU = projectedU;
            }
            if (projectedU > existing.maxU) {
                existing.maxU = projectedU;
            }
            if (collider.position.z < existing.minZ) {
                existing.minZ = collider.position.z;
            }
            if (collider.position.z > existing.maxZ) {
                existing.maxZ = collider.position.z;
            }
        }

        markers.push({
            relationId: collider.relationId,
            center: {
                x: projectedPoint.x - mid.x,
                y: projectedPoint.y - mid.y,
                z: zOffset - (projectedZ - zOffset) * Z_SCALE - mid.z
            },
            normal: { x: frame.normal.x, y: frame.normal.y, z: frame.normal.z },
            u: { x: frame.uAxis.x, y: frame.uAxis.y, z: frame.uAxis.z },
            v: { x: frame.vAxis.x, y: frame.vAxis.y, z: frame.vAxis.z },
            width: 0,
            height: 0,
            groupId: clampGroupIdByRelationId.get(collider.relationId) ?? null,
            renderOrder: 0,
            offsetToPlane: Math.abs(signedDistance)
        });

        const clampRotation = clampRotationByRelationId.get(collider.relationId) ?? 0;
        if (Math.abs(clampRotation) > EPSILON) {
            axisRotation.setFromAxisAngle(frame.normal, clampRotation);
            rotatedUAxis.copy(frame.uAxis).applyQuaternion(axisRotation);
            rotatedVAxis.copy(frame.vAxis).applyQuaternion(axisRotation);
            markers[markers.length - 1].u = { x: rotatedUAxis.x, y: rotatedUAxis.y, z: rotatedUAxis.z };
            markers[markers.length - 1].v = { x: rotatedVAxis.x, y: rotatedVAxis.y, z: rotatedVAxis.z };
        }
    }

    const relationDimensions = new Map<string, { width: number; height: number }>();
    for (const [relationId, extent] of relationExtents) {
        relationDimensions.set(relationId, {
            width: Math.max((extent.maxU - extent.minU), MIN_MARKER_WIDTH),
            height: Math.max((extent.maxZ - extent.minZ) * Z_SCALE, MIN_MARKER_HEIGHT)
        });
    }

    for (const marker of markers) {
        const markerSize = relationDimensions.get(marker.relationId);
        marker.width = markerSize?.width ?? MIN_MARKER_WIDTH;
        marker.height = markerSize?.height ?? MIN_MARKER_HEIGHT;
    }

    return markers;
};

export const useCreateClampDimensioning = ({
    instanceToGroupMap,
    articleColliders,
    selectedPoiIndex,
    mid,
    workingPlane,
    floorZ,
    poi
}: CreateClampDimensioningProps): ClampDimensioningResult => {
    const markers = useMemo(() => {
        const state = usePlacementStore.getState();

        if (!articleColliders || selectedPoiIndex === undefined || !workingPlane) {
            return [];
        }

        const frame = createWorkingPlaneFrame(workingPlane);
        if (!frame) {
            return [];
        }

        const poiArticles = poi.articles;
        if (!poiArticles) {
            return [];
        }

        const articles: Record<string, Article> = state.articles;
        const { clampRelationIds, clampRotationByRelationId, clampGroupIdByRelationId } = buildClampLookup(
            poiArticles,
            articles,
            instanceToGroupMap
        );

        if (clampRelationIds.size === 0) {
            return [];
        }

        const clampColliders = articleColliders.filter(
            (c) =>
                c.poiIndex === selectedPoiIndex &&
                clampRelationIds.has(c.relationId) &&
                !isPlacementIndicator(c.relationId, state)
        );

        if (clampColliders.length === 0) {
            return [];
        }

        const zOffset = floorZ - 0.3;
        const markers = buildClampMarkers(
            clampColliders,
            workingPlane,
            frame,
            mid,
            zOffset,
            clampRotationByRelationId,
            clampGroupIdByRelationId
        );

        if (markers.length === 0) {
            return [];
        }

        // Determine the min and max offsets to normalize render order
        let minOffset = Infinity;
        let maxOffset = -Infinity;
        for (const marker of markers) {
            if (marker.offsetToPlane < minOffset) {
                minOffset = marker.offsetToPlane;
            }
            if (marker.offsetToPlane > maxOffset) {
                maxOffset = marker.offsetToPlane;
            }
        }

        const offsetRange = maxOffset - minOffset;
        return markers.map((marker) => {
            const normalized = offsetRange <= EPSILON ? 0 : (marker.offsetToPlane - minOffset) / offsetRange;
            return {
                relationId: marker.relationId,
                center: marker.center,
                normal: marker.normal,
                u: marker.u,
                v: marker.v,
                width: marker.width,
                height: marker.height,
                groupId: marker.groupId,
                renderOrder: RENDER_ORDER_BASE + Math.round(normalized * (RENDER_ORDER_LAYERS - 1))
            };
        });
    }, [articleColliders, selectedPoiIndex, workingPlane, poi.articles, instanceToGroupMap, floorZ, mid]);

    if (markers.length === 0) {
        return defaultResult;
    }

    return { markers };
};
